"use client";

import type { ReportCatalog, ReportQuery } from "@gettactic/api";
import React from "react";

interface StarterQuestionsProps {
  catalog: ReportCatalog;
  onPick: (query: ReportQuery) => void;
}

export type Starter = { label: string; query: ReportQuery };

const last30: ReportQuery["period"] = { preset: "last_30_days" };

/** Ready-made questions, one or two per source; only those the catalog can answer are shown. */
export const STARTERS: Starter[] = [
  {
    label: "Occupancy by office",
    query: {
      measure: "occupancy",
      breakdown: ["office"],
      period: last30,
      compare: "previous_period"
    }
  },
  {
    label: "Busiest hours of the week",
    query: {
      measure: "reservations",
      breakdown: ["weekday", "hour"],
      period: last30
    }
  },
  {
    label: "Open vs dedicated desks",
    query: {
      measure: "occupancy",
      breakdown: ["access_mode"],
      period: last30,
      compare: "previous_period"
    }
  },
  {
    label: "Which amenities get booked",
    query: { measure: "occupancy", breakdown: ["amenity"], period: last30 }
  },
  {
    label: "Missed check-ins by team",
    query: {
      measure: "no_shows",
      breakdown: ["team"],
      period: last30,
      compare: "previous_period"
    }
  },
  {
    label: "Visitors per week",
    query: {
      measure: "visits",
      period: { preset: "last_90_days", grain: "week" },
      compare: "previous_period"
    }
  },
  {
    label: "When visitors arrive",
    query: { measure: "visits", breakdown: ["weekday", "hour"], period: last30 }
  },
  {
    label: "Invitations by office",
    query: {
      measure: "invitations",
      breakdown: ["office"],
      period: last30,
      compare: "previous_period"
    }
  },
  {
    label: "Deliveries by type",
    query: {
      measure: "deliveries",
      breakdown: ["delivery_type"],
      period: last30
    }
  },
  {
    label: "Remote days by weekday",
    query: { measure: "remote_days", breakdown: ["weekday"], period: last30 }
  },
  {
    label: "New users by office",
    query: {
      measure: "new_users",
      breakdown: ["office"],
      period: { preset: "last_90_days", grain: "week" },
      compare: "previous_period"
    }
  },
  {
    label: "Requests by category",
    query: {
      measure: "requests",
      breakdown: ["category"],
      period: last30,
      compare: "previous_period"
    }
  },
  {
    label: "Time to resolve by priority",
    query: {
      measure: "time_to_resolve",
      breakdown: ["priority"],
      period: last30
    }
  }
];

/** A row of starting points under the ask box: click one to load it. */
export const StarterQuestions: React.FC<StarterQuestionsProps> = ({
  catalog,
  onPick
}) => {
  const measures = new Map(catalog.measures.map((m) => [m.key, m]));
  const starters = STARTERS.filter((s) => {
    const measure = measures.get(s.query.measure);
    return (
      measure &&
      !measure.hidden &&
      (s.query.breakdown ?? []).every((d) => measure.dimensions.includes(d))
    );
  });
  if (starters.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-neutral-400 uppercase tracking-wider">
        Try
      </span>
      {starters.map((s) => (
        <button
          type="button"
          key={s.label}
          onClick={() => onPick(s.query)}
          className="rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[12px] text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
        >
          {s.label}
        </button>
      ))}
    </div>
  );
};
